<template>
  <div class="container">
    <h2 id="climate">3. Climate</h2>
    <div class="row">
      <div class="col">
        <h3>3.1 Temperature and Precipitation</h3>
        <p v-if="loaded">
            <span>
              Current temperature and precipitation data is obtained from the WorldClim climate database [<a href="#ref-4">4</a>], 
              which is a gridded set of global climate data with a spatial resolution of 
              1 sq. km.
            </span>
        </p>
        <p v-if="loaded">
            <span> 
              The average precipitation in the area is <strong>{{ factsheet.data.worldclim_bioclim.bioclim.BIO12.value.toFixed(0) }}mm</strong> per year,  
              and there is 
                <span v-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value > 90">an extremely high</span>
                <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value > 70">a very high</span>
                <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value >= 50">a high</span>
                <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value < 50">a low</span>
            </span>
            <span> variation throughout the year, with the wettest month receiving an average of <strong>{{ factsheet.data.worldclim_bioclim.bioclim.BIO13.value.toFixed(0) }}mm</strong>, and the driest month receiving <strong>{{ factsheet.data.worldclim_bioclim.bioclim.BIO14.value.toFixed(0) }}mm</strong>.
            </span>
        </p>
        <p v-if="loaded">
          The average temperature is <strong>{{ factsheet.data.worldclim_bioclim.bioclim.BIO1.value.toFixed(0) }} degrees Celcius</strong>, with a 
            <span v-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value > 500">very high</span>
            <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value > 250">high</span>
            <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value < 250">low</span>
            <span v-else-if="factsheet.data.worldclim_bioclim.bioclim.BIO15.value < 100">very low</span> variation throughout the year.
        </p>

      </div>
    </div>

    <div class="row">
      <div class="col text-center">
        <div style="background-color:#f5f5f5;" v-if="precipitationChart">

          <div style="font-size:0.8em;padding-top:15px;">Average Monthly Precipitation (mm/month)</div>
          <vue-plotly :data="precipitationChart.data" :layout="precipitationChart.layout" :options="precipitationChart.options"/>

        </div>
      </div>
      <div class="col text-center">
        <div style="background-color:#f5f5f5;" v-if="temperatureChart">
          <div style="font-size:0.8em;padding-top:15px;">Average Monthly Temperature (degrees Celcius)</div>
          <vue-plotly :data="temperatureChart.data" :layout="temperatureChart.layout" :options="temperatureChart.options"/>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12">
        <RasterMap 
          backgrounds="streets,satellite,light,osm" 
          title="Overview of Climatic Variables" 
          attribution="Map: Geofolio. Data: WorldClim [4]. Backgrounds: Mapbox/Openstreetmap Contributors [13]." 
          v-bind:layout="{legendWidth:6, legendCols:2, descriptionWidth:6, order:['legend','description'] }"
          v-bind:location="location"
          v-bind:factsheet="factsheet" 
          v-bind:loaded="loaded" 
          v-bind:layers="layers">
          <p>Map of average annual precipitation created by calculating the sum of monthly averages.</p>
        </RasterMap>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <h3>3.2 Climate Change</h3>
        <p>The climate change section of this factsheet is under development. It will contain a short explanation of climate change scenarios, as well as an overview of the most important effects on this area of interest.</p>
      </div>

    </div> 


  </div>
</template>

<script>
/* eslint-disable */
import Leaflet from 'leaflet';
import "leaflet/dist/leaflet.css";
import LeafletDraw from 'leaflet-draw'
import "leaflet-draw/dist/leaflet.draw.css";
import omnivore from "@mapbox/leaflet-omnivore";
import _ from 'lodash';
import VuePlotly from '@statnett/vue-plotly'

import RasterMap from '@/components/factsheet/RasterMap.vue'

export default {
    name: 'Climate',
    components:{ VuePlotly, RasterMap },
    props: ['location', 'factsheet', 'loaded'],
    data() {
        return {
            charts: {},
            precipitationChart: null,
            temperatureChart: null,
            layers: [
              {'title': 'Precipitation', 'source': '/worldclim_precipitation/prec-annual.tif'},
            ]
        }
    },
    created() {
    },
    mounted() {
      if(this.loaded) {
        this.createCharts()
      }
    },
    computed: {

    },
    watch: {
        loaded: function() {
          console.log("clim loaded is:"+this.loaded)
          this.createCharts()
        }
    },
    methods: {
      createCharts() {
        console.log("creating climate charts...")
        this.precipitationChart = {
          data: [{
            x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            y: this.factsheet.data.worldclim_precipitation.monthly_prec,
            type: 'bar',
            hoverinfo: 'skip'
          }],
            layout: {
            autosize: true,
            showlegend:false,
            height:300,
            margin: { l: 40, r: 15, b: 30, t: 5, pad: 5},
            paper_bgcolor: '#f5f5f5',
            plot_bgcolor: '#f5f5f5'
          },
          options: {
            staticPlot: true,
            responsive: true
          }
        }

        this.temperatureChart = {
          data: [
            {
              x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              y: this.factsheet.data.worldclim_temperature.monthly_tavg,
              mode: 'lines'
            },
            {
              x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              y: this.factsheet.data.worldclim_temperature.monthly_tmin,
              mode: 'lines',
              line: { color: '#1f77b4', dash: 'dot' }
            },
            {
              x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              y: this.factsheet.data.worldclim_temperature.monthly_tmax,
              mode: 'lines',
              line: { color: '#1f77b4', dash: 'dot' }
            }
          ],
          layout: {
            autosize: true,
            showlegend:false,
            height:300,
            margin: { l: 40, r: 15, b: 30, t: 5, pad: 5},
            paper_bgcolor: '#f5f5f5',
            plot_bgcolor: '#f5f5f5'
          },
          options: {
            staticPlot: true,
            responsive: true
          }
        }
      }
    }
}
</script>

<style>

</style>